/**
 * Badge — small status / category pill.
 *
 * Two use-sites today:
 *   - Domain badges (Audio, Process, ...) — domain-colored.
 *   - Status chips (Signed, Running, Published, ...) — semantic.
 */

import type { ReactNode } from 'react';

import type { GiStatus, NormalizedDomain } from '@/api/types';
import { cn } from '@/utils/cn';

const DOMAIN_CLASS: Record<NormalizedDomain, string> = {
  audio: 'bg-domain-audio-soft text-domain-audio',
  process: 'bg-domain-process-soft text-domain-process',
  medical: 'bg-domain-medical-soft text-domain-medical',
  financial: 'bg-domain-financial-soft text-domain-financial',
  defense: 'bg-domain-defense-soft text-domain-defense',
  government: 'bg-domain-government-soft text-domain-government',
  generic: 'bg-domain-generic-soft text-domain-generic',
  conductor: 'bg-domain-conductor-soft text-domain-conductor',
};

const STATUS_CLASS: Record<GiStatus, string> = {
  signed: 'bg-spark-soft text-spark',
  running: 'bg-domain-process-soft text-domain-process',
  connected: 'bg-domain-government-soft text-domain-government',
  published: 'bg-domain-financial-soft text-domain-financial',
  forked: 'bg-domain-audio-soft text-domain-audio',
  deprecated: 'bg-domain-generic-soft text-domain-generic',
  revoked: 'bg-domain-medical-soft text-domain-medical',
};

export function DomainBadge({
  domain,
  label,
}: {
  domain: NormalizedDomain;
  label: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
        DOMAIN_CLASS[domain],
      )}
    >
      {label}
    </span>
  );
}

export function StatusBadge({ status, label }: { status: GiStatus; label: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        STATUS_CLASS[status],
      )}
    >
      <span
        aria-hidden="true"
        className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80"
      />
      {label}
    </span>
  );
}
